import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import '../style.css';
import { Breadcrumbs } from '../../../components/Atom/breadcumbs';
import { ArrowLeftOutlined, MailOutlined, ScheduleOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ShortenId from 'components/Atom/ShortenId';
import StatusTag from 'components/Atom/StatusTag';
import { Button, Button as ANTButton, Empty, Form, Modal, Radio, Skeleton, Input, Checkbox } from 'antd';
import { useSelector } from 'react-redux';
import { getSingleInvoice, sendInvoicePaymentLink, markPaidSingleInvoice, emailInvoicePdf } from 'api/subscriptions';
import { useQuery } from 'react-query';
import { capitalizeWords } from 'utils/helper';
import { checkUserPermissions, formatAmount } from 'utils/services';
import { USER_ROLES, TIMEZONES } from 'utils/constant';
import PricingModelBreakdown from './assets/PricingModelBreakdown';
import LoadingSkeleton from '../../../components/Skeleton/index';
import { ResetButton } from '../../../components/Atom/Button';
import { formatInTimeZone } from 'date-fns-tz';

export const ViewInvoiceDetails = () => {
  const [form] = Form?.useForm();
  const [isInvoicePaid, setIsInvoicePaid] = useState(false);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('invoice_customer');
  const [type, setType] = useState(null);
  const [isPaymentLinkSending, setIsPaymentLinkSending] = useState(false);
  const [isInvoicePdfSending, setIsInvoicePdfSending] = useState(false);
  const location = useLocation();
  const { id } = useParams();
  const store = useSelector((state) => state.company.store);
  const navigate = useNavigate();
  // const [record, setRecord] = useState(location?.state?.record);
  const fetchInvoiceData = async () => {
    const response = await getSingleInvoice(location?.state?.record?.store_id || store?._id, id || location?.state?.id);
    return response?.data?.data || null;
  };
  const {
    data: record,
    refetch,
    isLoading,
    isFetching,
  } = useQuery(`invoices_${id || location?.state?.record?.invoice_id}`, fetchInvoiceData, {
    enabled: false,
    manual: true,
  });
  // useEffect(() => {
  //   setRecord(location?.state?.record);
  // }, [store]);
  useEffect(() => {
    refetch();
  }, [store]);

  const handleRadioChange = (e) => {
    setMode(e.target.value);
  };

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleInvoicePaid = async (values) => {
    setIsInvoicePaid(true);

    const response = await markPaidSingleInvoice(record?.store_id, id || location?.state?.record?.invoice_id);
    if (response?.data?.success) {
      refetch();
    }

    setIsInvoicePaid(false);
  };

  const initialValues = {
    email: '',
    name: '',
    phone: '',
  };

  const markPaidInvoiceInitialValues = {
    update_subscription: false,
  };

  const handleSubmit = async (values) => {
    if (type === 'payment_link') {
      handleSendPaymentLink(values);
    } else if (type === 'invoice_pdf') {
      handleEmailInvoicePdf(values);
    }
  };

  const handleSendPaymentLink = async (values) => {
    setIsPaymentLinkSending(true);

    const payload = {
      invoice_id: id || location?.state?.record?.invoice_id,
      mode: mode,
      ...(mode === 'customer' && {
        customer: {
          email: values?.email,
          name: values?.name,
          phone: values?.phone,
        },
      }),
    };
    const response = await sendInvoicePaymentLink(record?.store_id, payload);
    if (response?.data?.success) {
      refetch();
    }
    setIsPaymentLinkSending(false);
    handleOpen();
    setType(null);
  };

  const handleEmailInvoicePdf = async (values) => {
    setIsInvoicePdfSending(true);

    const payload = {
      invoice_id: id || location?.state?.record?.invoice_id,
      mode: mode,
      ...(mode === 'customer' && {
        customer: {
          email: values?.email,
          name: values?.name,
          phone: values?.phone,
        },
      }),
    };
    const response = await emailInvoicePdf(record?.store_id, payload);
    if (response?.data?.success) {
      refetch();
    }
    setIsInvoicePdfSending(false);
    handleOpen();
    setType(null);
  };

  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'Invoice',
        href: '/subscriptions/invoice',
        data: { id: location?.state?.id, isRedirection: true, filtersData: location?.state?.filtersData, searchValue: location?.state?.searchValue },
      },
      {
        name: <ShortenId value={id || location?.state?.id || 'Not Found'} />,
      },
    ],
  };
  const time_zone = store?.settings?.timezone || TIMEZONES.PKT;
  const generalInformation = [
    { label: 'Invoice ID', value: <ShortenId value={record?.invoice_id} /> },
    { label: 'Plan ID', value: <ShortenId value={record?.plan_id} /> },
    { label: 'Subscription ID', value: <ShortenId value={record?.subscription_id?.subscription_id || record?.subscription_id} /> },
    { label: 'Status', value: record?.state ? <StatusTag tag={record?.state} /> : <StatusTag tag={'pending'} /> },
    { label: 'Last Update Time', value: record?.updatedAt ? formatInTimeZone(record?.updatedAt, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
    ...(record?.combination_operator ? [{ label: 'Combination Operator', value: record?.combination_operator || 'N/A' }] : []),
    ...(record?.or_strategy ? [{ label: 'Whichever Strategy', value: record?.or_strategy || 'N/A' }] : []),
    ...(record?.is_csv_based ? [{ label: 'CSV Based', value: record?.is_csv_based ? 'Yes' : 'No' }] : []),
    ...(record?.csv_amount ? [{ label: 'CSV Amount', value: record?.csv_amount || 'N/A' }] : []),
    ...(record?.components?.[0]?.base_amount ? [{ label: 'Base Amount', value: record?.components?.[0]?.base_amount?.toLocaleString('en-US', { maximumSignificantDigits: 21 }) || 'N/A' }] : []),
  ];

  const paymentDetails = [
    { label: 'Payment Token', value: <ShortenId value={record?.subscription?.payment?.token || record?.payment?.token || 'N/A'} /> },
    { label: 'Total Amount', value: formatAmount(record?.amount || 0, record?.currency) || '--' },
    { label: 'Currency', value: record?.currency || 'N/A' },
    { label: 'Payment Date', value: record?.payment_date ? formatInTimeZone(record?.payment_date, time_zone, 'MMM d, yyyy h:mm:ss a') : 'N/A' },
  ];

  return (
    <>
      <div className='header-container p-4 custom-sticky'>
        <div className='payment-breadcrums'>
          <div
            className='back-button'
            onClick={() =>
              navigate('/subscriptions/invoice', {
                state: {
                  id: location?.state?.id,
                  isRedirection: true,
                  filtersData: location?.state?.filtersData,
                  searchValue: location?.state?.searchValue,
                },
              })
            }>
            <ArrowLeftOutlined /> <span style={{ fontWeight: 600 }}>Back</span>
          </div>
          <span style={{ color: '#8C8C8C' }}>|</span>
          <Breadcrumbs {...breadcrumbProps} />
          <ResetButton onClick={refetch} loading={isFetching} />
        </div>
        <div className='detail-heading mt-4'>Invoice Detail</div>
      </div>
      <div className='subscription-details logs-details'>
        {isLoading ? (
          <LoadingSkeleton />
        ) : !record ? (
          store?.isSelected ? (
            <div className='empty-custom-style'>
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            <div className='empty-custom-style'>
              <Empty description={'Select store from the drop-down menu above'} />
            </div>
          )
        ) : (
          <>
            <div className='details-section '>
              <div className='heading-section'>
                <h3>Invoice Detail</h3>
              </div>
              <div className='detail-card'>
                {generalInformation?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>Payment Details</h3>
              </div>
              <div className='detail-card'>
                {paymentDetails?.map((info, index) => (
                  <div key={index} className='detail-value'>
                    <h2>{info?.label}</h2> <span>{info?.value || 'N/A'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className='details-section'>
              <div className='heading-section'>
                <h3>Customer Details</h3>
              </div>
              <div className='detail-card'>
                <div className='detail-value'>
                  <h2>Name</h2> <span>{record?.subscription?.customer?.name || record?.customer?.name || 'N/A'}</span>
                </div>
                <div className='detail-value'>
                  <h2>Email</h2> <span>{record?.subscription?.customer?.email || record?.customer?.email || 'N/A'}</span>
                </div>
                <div className='detail-value'>
                  <h2>Phone</h2> <span>{record?.subscription?.customer?.phone || record?.customer?.phone || 'N/A'}</span>
                </div>
              </div>
            </div>

            {record?.is_csv_based && record?.csvBasedAdditionalCharges && record?.csvBasedAdditionalCharges?.length > 0 && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Additional Charges</h3>
                </div>
                <div className='detail-card'>
                  {record?.csvBasedAdditionalCharges?.map((charge) => (
                    <Fragment key={charge?.key}>
                      <div className='detail-value'>
                        <span>{capitalizeWords(charge?.key)}</span>
                      </div>
                      <div className='detail-value'>
                        <span>
                          {charge?.value || 'N/A'}
                          {charge?.type === 'percentage' ? '%' : ''}
                        </span>
                      </div>
                      <div className='detail-value'>
                        <span>{charge?.inclusive ? 'Inclusive' : 'Exclusive'}</span>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* New Pricing Model Breakdown Structure */}
            {record?.pricing_model_breakdown && record?.pricing_model_breakdown?.length > 0 && (
              <div className='details-section'>
                <div className='heading-section'>
                  <h3>Pricing Breakdown</h3>
                </div>
                {record?.pricing_model_breakdown?.map((pricingModel, idx) => (
                  <PricingModelBreakdown key={pricingModel?.pricing_model_id || idx} pricingModel={pricingModel} index={idx} currency={record?.currency} />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Modal trigger='click' centered open={open} width={window.innerWidth < 768 ? 350 : 600} footer={null} destroyOnClose onCancel={handleOpen}>
        <div className='mobileVersion'>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            <span className='createIcon'>
              <ScheduleOutlined />
            </span>
          </div>
          <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
            {type === 'payment_link' ? 'Send Payment Link To Customer' : 'Email Invoice PDF To Customer'}
          </div>
          <div className='radio-button' style={{ marginBottom: '12px' }}>
            <Radio.Group name='radiogroup' onChange={handleRadioChange} value={mode}>
              <Radio style={{ fontWeight: '400' }} value={'invoice_customer'}>
                Invoice Customer
              </Radio>
              <Radio style={{ fontWeight: '400' }} value={'customer'}>
                Customer
              </Radio>
            </Radio.Group>
          </div>
          <div className='formDetails'>
            <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={initialValues}>
              {mode === 'customer' && (
                <div className='form-inputs'>
                  <Form.Item
                    name='email'
                    label='Email'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        required: true,
                        message: 'Email is required',
                      },
                      {
                        type: 'email',
                        message: 'Please enter a valid email address',
                      },
                    ]}>
                    <Input placeholder='Enter Email' name='email' className='input-placeholder' suffix={<MailOutlined style={{ color: '#7F7D83' }} />} />
                  </Form.Item>
                  <Form.Item
                    name='name'
                    label='Name'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        required: true,
                        message: 'Name is required',
                      },
                      {
                        type: 'string',
                        message: 'Please enter a valid name',
                      },
                    ]}>
                    <Input placeholder='Enter Name' name='name' className='input-placeholder' suffix={<UserOutlined style={{ color: '#7F7D83' }} />} />
                  </Form.Item>
                  <Form.Item
                    name='phone'
                    label='Phone'
                    className='customInput mobileInputView'
                    rules={[
                      {
                        required: true,
                        message: 'Phone number is required',
                      },
                      {
                        type: 'string',
                        message: 'Please enter a valid phone number',
                      },
                    ]}>
                    <Input placeholder='Enter Phone Number' name='phone' className='input-placeholder' suffix={<PhoneOutlined style={{ color: '#7F7D83' }} />} />
                  </Form.Item>
                </div>
              )}
              <div className='mb-[-20px] form_button update-button'>
                <Form.Item>
                  <div style={{ display: 'flex', justifyContent: 'right' }}>
                    <ANTButton
                      block
                      className='rounded-lg'
                      onClick={() => {
                        handleOpen();
                        setMode('invoice_customer');
                        setType(null);
                      }}>
                      Cancel
                    </ANTButton>
                    <Button block className='button-theme ml-4 rounded-lg' htmlType='submit' loading={isPaymentLinkSending || isInvoicePdfSending}>
                      Send
                    </Button>
                  </div>
                </Form.Item>
              </div>
            </Form>
          </div>
        </div>
      </Modal>
      {checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN, USER_ROLES.XPAY_SUBSCRIPTION_USER]) && (
        <div className={`flex items-center gap-4 ${isLoading || record ? 'footer footer-action-button' : ''}`}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }} className='subscription-button'>
              <Skeleton.Input active style={{ marginTop: '0' }} className='first-button' />
              <Skeleton.Input active style={{ marginTop: '0' }} className='second-button' />
            </div>
          ) : (
            record && (
              <>
                {record?.state !== 'paid' && (
                  <div>
                    <Button
                      className='primaryBtn table-action-btn flex items-center justify-center'
                      block
                      onClick={() => {
                        handleInvoicePaid();
                      }}
                      loading={isInvoicePaid}>
                      Mark as Paid
                    </Button>
                  </div>
                )}

                {record?.state !== 'paid' && (
                  <div>
                    <Button
                      className='primaryBtn table-action-btn flex items-center justify-center'
                      block
                      onClick={() => {
                        setType('payment_link');
                        handleOpen();
                      }}
                      loading={isPaymentLinkSending}>
                      Send Payment Link
                    </Button>
                  </div>
                )}

                {/* TODO: Uncomment this when the invoice PDF feature is needed */}
                {record?.pricing_model_breakdown && record?.pricing_model_breakdown?.length > 0 && (
                  <div>
                    <Button
                      className='primaryBtn table-action-btn flex items-center justify-center'
                      block
                      onClick={() => {
                        setType('invoice_pdf');
                        handleOpen();
                      }}
                      loading={isInvoicePdfSending}>
                      Email Invoice PDF
                    </Button>
                  </div>
                )}
              </>
            )
          )}
        </div>
      )}
    </>
  );
};
